import mapboxgl from "mapbox-gl";

import { Configuration, ConfigurationUpsert, GroupConfig, MultiProject, MultiProjectArea, MultiProjectAreaStatistic } from "fond/types";
import { FilterConfiguration, LayerConfig, LayerStyle, SublayerConfig } from "fond/types/ProjectLayerConfig";
import { pickIDs } from "fond/utils";
import { formatCompactNumber } from "fond/utils/number";

import { boundaryConfiguration } from "./boundaryConfiguration";
import { cbgOverlayConfiguration, defaultCbgFilteredLayerIds } from "./cbgOverlayConfiguration";
import { defaultDensityFilteredLayerIds, densityOverlayConfiguration } from "./densityOverlayConfiguration";
import { defaultFccFilteredLayerIds, fccOverlayConfiguration } from "./fccOverlayConfiguration";
import { defaultSoilFilteredLayerIds, soilOverlayConfiguration } from "./soilOverlayConfiguration";
import { defaultSubareaFilteredLayerIds, subareaConfiguration, SubareaLayerId } from "./subareaConfiguration";

export interface FamilialLayerConfig {
  config: LayerConfig;
  descendants: (LayerStyle | SublayerConfig)[];
}

export enum OverlayIds {
  ROOT = "multiProjectOverlayGroup",
  FCC = "multiProjectFccGroup",
  DEMOGRAPHICS = "multiProjectCbgGroup",
  DENSITIES = "multiProjectDensityGroup",
  SOIL = "multiProjectSoilGroup",
}

export const defaultCityPlannerLayerFilters = {
  [OverlayIds.FCC]: defaultFccFilteredLayerIds,
  [OverlayIds.DEMOGRAPHICS]: defaultCbgFilteredLayerIds,
  [OverlayIds.DENSITIES]: defaultDensityFilteredLayerIds,
  [OverlayIds.SOIL]: defaultSoilFilteredLayerIds,
  [SubareaLayerId.ROOT]: defaultSubareaFilteredLayerIds,
};

/**
 * Generate the Configuration upsert based on the Multiproject Boundary & Areas.
 * A LayerConfig is generated for the Boundary & one for each Area.
 */
const multiProjectConfiguration = (
  { Areas, ID, SystemOfMeasurement }: Pick<MultiProject, "ID" | "SystemOfMeasurement"> & { Areas: Pick<MultiProjectArea, "ID" | "Name">[] },
  statistics: MultiProjectAreaStatistic[],
  providers?: string[]
): ConfigurationUpsert => {
  const boundary = boundaryConfiguration(ID);
  const subareas = subareaConfiguration(Areas, statistics);

  const { configs: fccConfigs, groupConfig: fccGroup } = fccOverlayConfiguration(providers);
  const { configs: cbgConfigs, groupConfig: cbgGroup } = cbgOverlayConfiguration();
  const { configs: densityConfigs, groupConfig: densityGroup } = densityOverlayConfiguration(SystemOfMeasurement);
  const { configs: soilConfigs, groupConfig: soilGroup } = soilOverlayConfiguration(SystemOfMeasurement);
  const overlayGroup = groupConfigTemplate(OverlayIds.ROOT, [fccGroup, cbgGroup, densityGroup, soilGroup], "Overlays", false);

  return [
    ...subareas,
    ...boundary,
    overlayGroup,
    ...fccConfigs,
    fccGroup,
    ...cbgConfigs,
    cbgGroup,
    ...densityConfigs,
    densityGroup,
    ...soilConfigs,
    soilGroup,
  ];
};

/**
 * Takes the phases and generates the style configur ation for the report build order map.
 */
export const generateMultiProjectMapConfiguration = (
  multiProject: Pick<MultiProject, "ID" | "SystemOfMeasurement"> & { Areas: Pick<MultiProjectArea, "ID" | "Name">[] },
  statistics: MultiProjectAreaStatistic[],
  providers?: string[]
): Configuration => {
  const data = multiProjectConfiguration(multiProject, statistics, providers);

  return {
    ID: "",
    Key: "",
    SourceID: "",
    Data: {
      ids: pickIDs(data),
      entities: Object.fromEntries(data.map((entity) => [entity.ID, entity])),
    },
    MapChildren: [...pickIDs(data.filter((entity) => entity.Type === "GROUP" && entity.ParentID === null))],
    Type: "MapLayerConfig",
  };
};

export const groupConfigTemplate = (
  id: string,
  children: Array<LayerConfig | GroupConfig>,
  label: string,
  visible = true,
  parentId?: string
): GroupConfig => ({
  ID: id,
  Type: "GROUP",
  Label: label,
  Key: null,
  Children: pickIDs(children),
  IsVisible: visible,
  Hidden: false,
  GlobalPosition: 0,
  Position: 0,
  ParentID: parentId ?? null,
  RootID: null,
});

export const layerConfigTemplate = (
  id: string,
  key: string,
  label: string,
  styles: LayerStyle[],
  children?: string[],
  parentId?: string
): LayerConfig => ({
  ID: id,
  Label: label,
  IsVisible: true,
  Hidden: false,
  Styles: pickIDs(styles),
  Position: 0,
  Type: "LAYER",
  GeometryType: "Polygon",
  GlobalPosition: 0,
  ParentID: parentId ?? null,
  Key: key,
  MaxZoomLevel: null,
  MinZoomLevel: null,
  Children: children ?? [],
});

export const sublayerTemplate = (
  id: string,
  key: string,
  layerId: string,
  label: string,
  filter: FilterConfiguration | null,
  styles: LayerStyle[]
): SublayerConfig => ({
  Type: "SUBLAYER",
  ParentID: layerId,
  FilterConfiguration: filter,
  Styles: pickIDs(styles),
  ID: id,
  Label: label,
  Key: key,
  IsVisible: true,
  Hidden: false,
  Position: 0,
  GeometryType: "Polygon",
});

interface BucketConfiguration {
  id: string;
  label: string;
  color: string;
  filter: FilterConfiguration;
}

interface MakeBucketOptions {
  layerId: string;
  labels?: string[];
  attribute: string | mapboxgl.Expression;
  splits: number[];
  colors: string[];
  includeLastBucket?: boolean;
  includeNullBucket?: boolean;
}

/**
 * Generate the buckets for a layer based on the splits and colors provided.
 */
export const makeBuckets = ({
  layerId,
  labels,
  attribute,
  splits,
  colors,
  includeLastBucket,
  includeNullBucket,
}: MakeBucketOptions): BucketConfiguration[] => {
  const attributeExpression = typeof attribute === "string" ? ["get", attribute] : attribute;
  const resolvedLabels = labels ?? splits.map((n) => formatCompactNumber(n));
  if (splits.length !== resolvedLabels.length) {
    throw new Error("The number of labels must match the number of splits.");
  }

  const buckets: BucketConfiguration[] = splits.slice(0, splits.length - 1).map((n, index) => {
    const endpoint = splits[index + 1];

    return {
      id: `${layerId}-${n}-${endpoint}`,
      label: `${resolvedLabels[index]} - ${resolvedLabels[index + 1]}`,
      color: colors[index],
      filter: {
        Mapbox: ["all", [">=", attributeExpression, n], ["<", attributeExpression, endpoint]],
        Type: "group" as const,
      },
    };
  });

  if (includeLastBucket) {
    const lastEndpoint = splits[splits.length - 1];
    const lastBucket = {
      id: `${layerId}-${lastEndpoint}+`,
      label: `${resolvedLabels[splits.length - 1]}+`,
      color: colors[colors.length - 1],
      filter: {
        Mapbox: [">=", attributeExpression, lastEndpoint],
        Type: "expression" as const,
      },
    };
    buckets.push(lastBucket);
  }
  if (includeNullBucket) {
    const nullBucket = {
      id: `${layerId}-null`,
      label: "N/A",
      color: "#8a8a8a",
      filter: {
        // Vector tile attributes cannot be null; the corresponding attribute is simply omitted
        // if the value is null.
        Mapbox: ["==", attributeExpression, null],
        Type: "expression" as const,
      },
    };
    buckets.push(nullBucket);
  }
  return buckets;
};
